import { AutoAllocatingTranche } from 'further-types/allocations';
import ExistingDraftAllocationStatus from 'components/ExistingDraftAllocationStatus';
import { Warning, CheckCircleOutline, ErrorOutline } from '@mui/icons-material';
import { Box, SelectChangeEvent, Theme, Tooltip, styled } from '@mui/material';
import {
  AppCheckBox,
  AppNumberInput,
  AppTextInput,
  AppSelectBox,
} from 'components/FormElements';
import { NumericFormat } from 'react-number-format';
import {
  CalendarDay,
  numberToCurrencyString,
  numberToDisplayString,
  numberToPercentageStringWithLimits,
  pluralize,
} from 'further-ui/utils';
import { InitialTargetShareOption } from 'further-types/allocations';
import { InitialTargetShareOptionLabels } from 'further-ui/labels';
import { Row } from 'components/Layout';
import calculateTargetValues from './calculate-target-values';
import getMinimumInvestorPortfolioSizeWarning from './get-minimum-investor-portfolio-size-warning';
import getPortfolioSizeDifferenceWarning from './get-portfolio-size-difference-warning';
import PortfolioSizeTooltip from './PortfolioSizeTooltip';
import { FundAllocation } from '..';
import { isNumber } from 'lodash';

const StyledAppNumberInput = styled(AppNumberInput)(
  ({ theme, percentEndAdornment = false, disabled = false }) => ({
    '& .MuiInputBase-input': {
      textAlign: 'end',
      marginRight: percentEndAdornment ? theme.spacing(1) : 0,
    },
    '& .MuiInputAdornment-root': {
      marginRight: 0,
    },
    width: '80%',
    backgroundColor: disabled ? theme.palette.grey[100] : 'inherit',
  }),
);

const StyledAppTextInput = styled(AppTextInput)(() => ({
  '& .MuiInputBase-input': {
    textAlign: 'end',
  },
  width: '80%',
}));

const StyledAppSelectBox = styled(AppSelectBox)(({ theme }) => ({
  width: '80%',
  fontWeight: 600,
  '& .MuiSelect-select': {
    fontSize: theme.fontSizes?.sm,
    lineHeight: '22px',
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

const IconContainer = styled(Box)(({ theme }) => ({
  justifyContent: 'center',
  minHeight: '36px',
  paddingTop: theme.spacing(1.5),
}));

const StyledWarningIcon = styled(Warning)(({ theme }) => ({
  color: theme.palette.background.info,
}));

const Value = styled('div', {
  shouldForwardProp: (prop) =>
    !['bold', 'minHeight', 'large', 'wrap'].includes(prop.toString()),
})<{
  bold?: boolean;
  minHeight?: string;
  large?: boolean;
  wrap?: boolean;
}>(({ theme, bold, minHeight, large, wrap }) => ({
  fontSize: large ? theme.fontSizes?.lg : theme.fontSizes?.regular,
  color: theme.palette.text.darkBodyText,
  fontWeight: bold
    ? theme.typography.fontWeightBold
    : theme.typography.fontWeightRegular,
  minHeight,
  whiteSpace: wrap ? 'normal' : 'nowrap',
}));

const initialTargetSharesOptions = [
  {
    value: InitialTargetShareOption.InitialTargetShares,
    label:
      InitialTargetShareOptionLabels[
        InitialTargetShareOption.InitialTargetShares
      ],
  },
  {
    value: InitialTargetShareOption.InitialTargetInvestment,
    label:
      InitialTargetShareOptionLabels[
        InitialTargetShareOption.InitialTargetInvestment
      ],
  },
  {
    value: InitialTargetShareOption.InitialTargetAllocationPercent,
    label:
      InitialTargetShareOptionLabels[
        InitialTargetShareOption.InitialTargetAllocationPercent
      ],
  },
  {
    value: InitialTargetShareOption.InitialTargetInvestmentAndShares,
    label:
      InitialTargetShareOptionLabels[
        InitialTargetShareOption.InitialTargetInvestmentAndShares
      ],
  },
];

type GetAutoAllocationTableFieldsParams = {
  handleTrancheFieldChange: (
    trancheId: string,
    field: keyof AutoAllocatingTranche,
    value: string | number | boolean | Record<string, unknown>,
  ) => void;
  initialTargetShareSelected: InitialTargetShareOption;
  setInitialTargetShareSelected: (value: InitialTargetShareOption) => void;
  resetInitialTargetShares: () => void;
  dealSharePrice: number;
  theme: Theme;
  fundAutoAllocationResults: Array<FundAllocation>;
};

const getTargetTooltip = (type: 'shares' | 'investment') => {
  const valueType = type === 'shares' ? 'number of shares' : 'investment';
  return (
    <>
      This is the theoretical maximum {valueType} this tranche can invest. The
      actual {valueType} may vary in the next step, depending on each individual
      investor's remaining funds.
    </>
  );
};

const getAutoAllocationTableFields = ({
  handleTrancheFieldChange,
  initialTargetShareSelected,
  setInitialTargetShareSelected,
  resetInitialTargetShares,
  dealSharePrice,
  theme,
  fundAutoAllocationResults = [],
}: GetAutoAllocationTableFieldsParams) => {
  const lookupPreviousAllocation = (trancheId: string) => {
    return fundAutoAllocationResults.find(
      (fund) => fund.trancheId === trancheId,
    );
  };

  return [
    {
      key: 'tranche',
      label: '',
      render: (value: string) => {
        return (
          <Value minHeight="42px" bold wrap>
            {value}
          </Value>
        );
      },
    },
    ...(fundAutoAllocationResults?.length > 0
      ? [
          {
            key: 'previousAllocationInputAndOutput',
            label: (
              <Value large bold>
                Previous allocation input and output
              </Value>
            ),
            hasBackground: true,
          },
          {
            key: 'minimumInvestorPortfolioSizePrevious',
            label: 'Minimum investor portfolio size',
            hasBackground: true,
            render: (_, row: AutoAllocatingTranche) => {
              const previousAllocation = lookupPreviousAllocation(row._id);
              if (!previousAllocation) return <Value>-</Value>;

              return (
                <Value>
                  {previousAllocation.minimumInvestorPortfolioSize
                    ? `${previousAllocation.minimumInvestorPortfolioSize} holdings`
                    : '-'}
                </Value>
              );
            },
          },
          {
            key: 'maximumSingleCompanyAllocationPrevious',
            label: 'Maximum single company allocation',
            hasBackground: true,
            render: (_, row: AutoAllocatingTranche) => {
              const previousAllocation = lookupPreviousAllocation(row._id);
              if (!previousAllocation) return <Value>-</Value>;

              return (
                <Value>
                  {previousAllocation.maximumSingleCompanyAllocation
                    ? `${previousAllocation.maximumSingleCompanyAllocation} %`
                    : '-'}
                </Value>
              );
            },
          },
          {
            key: 'investmentsAllocatedToPrevious',
            label: 'Investors in allocation',
            hasBackground: true,
            render: (_, row: AutoAllocatingTranche) => {
              const previousAllocation = lookupPreviousAllocation(row._id);
              if (!previousAllocation) return <Value>-</Value>;

              const investmentsAllocatedTo =
                previousAllocation.investmentsAllocatedTo;
              const investmentsInTranche =
                previousAllocation.investmentsInTranche;

              const allInvestorsAllocated =
                investmentsAllocatedTo === investmentsInTranche;

              return (
                <Row justify="flex-end" spacing="xs" centered>
                  <Value>
                    {investmentsAllocatedTo}/{investmentsInTranche}
                  </Value>
                  {allInvestorsAllocated ? (
                    <CheckCircleOutline color="success" fontSize="small" />
                  ) : (
                    <Tooltip title="The previous allocation excluded some investors. This was caused by either a high share price or low amount of remaining tranche capital.">
                      <ErrorOutline color="error" fontSize="small" />
                    </Tooltip>
                  )}
                </Row>
              );
            },
          },
          {
            key: 'targetSharesPrevious',
            label: 'Shares allocated',
            hasBackground: true,
            render: (_, row: AutoAllocatingTranche) => {
              const previousAllocation = lookupPreviousAllocation(row._id);
              if (!previousAllocation) return <Value>-</Value>;

              const totalSharesAllocated =
                previousAllocation.totalSharesAllocated;
              const targetShares = previousAllocation.targetShares;

              const allSharesAllocated = totalSharesAllocated === targetShares;

              return (
                <Row justify="flex-end" spacing="xs" centered>
                  <Value wrap>
                    {numberToDisplayString(totalSharesAllocated)} /{' '}
                    {numberToDisplayString(targetShares)}
                  </Value>
                  {allSharesAllocated ? (
                    <CheckCircleOutline color="success" fontSize="small" />
                  ) : (
                    <Tooltip title="All target shares could not be allocated. This was caused by either a high share price or low amount of remaining tranche capital.">
                      <ErrorOutline color="error" fontSize="small" />
                    </Tooltip>
                  )}
                </Row>
              );
            },
          },
          {
            key: 'capitalAllocatedPrevious',
            label: 'Investment allocated',
            hasBackground: true,
            render: (_, row: AutoAllocatingTranche) => {
              const previousAllocation = lookupPreviousAllocation(row._id);
              if (!previousAllocation) return <Value>-</Value>;

              return (
                <Value>
                  {numberToCurrencyString(
                    previousAllocation.totalInvestmentExFee,
                  )}
                </Value>
              );
            },
          },
        ]
      : []),
    {
      key: 'existingDraftAllocation',
      label: 'Existing draft allocation',
      render: (value: boolean) => (
        <ExistingDraftAllocationStatus
          draft={value}
          tooltipTitle="There is currently a live draft allocation for this tranche. Proceeding to the next step with this auto-allocation will delete the previous draft allocation."
        />
      ),
    },
    {
      key: 'trancheHoldings',
      label: 'Tranche portfolio companies',
      render: (value: number) => <Value>{value}</Value>,
    },
    {
      key: 'trancheAllocations',
      label: 'Tranche unique allocations',
      render: (value: number) => <Value>{value}</Value>,
    },
    {
      key: 'trancheSubscriptions',
      label: 'Tranche subscriptions',
      render: (value: {
        count: number;
        containsSubscriptionsAfterInvestmentDate: boolean;
      }) => {
        return (
          <Row justify="flex-end" spacing="xs">
            <Value>{value.count}</Value>
            {value.containsSubscriptionsAfterInvestmentDate && (
              <Tooltip title="There are subscriptions in this tranche after the selected investment date. They will not be included in this allocation.">
                <StyledWarningIcon />
              </Tooltip>
            )}
          </Row>
        );
      },
    },
    {
      key: 'trancheCloseDate',
      label: 'Tranche close date',
      render: (value: CalendarDay | undefined) => (
        <Value>{value?.toLabel() ?? 'N/A'}</Value>
      ),
    },
    {
      key: 'totalTrancheCapital',
      label: 'Total tranche capital (£)',
      tooltip:
        'This is the total initial capital invested in the tranche by all investors, net of advice fees.',
      render: (value: number) => <Value>{numberToCurrencyString(value)}</Value>,
    },
    {
      key: 'totalTrancheInvestmentCapital',
      label: 'Total tranche investment capital (£)',
      tooltip:
        'This is the sum of tranche commitments, net of both advice fees and any initial and ongoing fees held back from investment.',
      render: (value: number) => <Value>{numberToCurrencyString(value)}</Value>,
    },
    {
      key: 'remainingTrancheInvestmentCapital',
      label: 'Remaining tranche investment capital (£)',
      tooltip:
        'This is the total tranche deployable capital, net of any capital already deployed into investments.',
      render: (value: number) => <Value>{numberToCurrencyString(value)}</Value>,
    },
    {
      key: 'remainingTrancheInvestmentCapitalPercent',
      label: 'Remaining tranche investment capital (%)',
      tooltip:
        'This is the investment capital remaining, represented as a percentage of total tranche investment capital.',
      render: (value: number) => <Value>{value.toFixed(2)}%</Value>,
    },
    {
      key: 'leastDiversifiedTrancheInvestor',
      label: 'Least diversified tranche investor',
      tooltip:
        'This is the number of completed investments that the least diversified investor in the tranche currently holds.',
      render: (value: number) => (
        <Value>{`${value} ${pluralize('holding', value)}`}</Value>
      ),
    },
    {
      key: 'minimumInvestorPortfolioSize',
      label: 'Minimum investor portfolio size',
      tooltip:
        'This is the minimum number of investments that tranche investors were told they would receive at the time they invested capital in the fund.',
      getWidth: (row: AutoAllocatingTranche) => {
        const { shouldShowWarning } = getMinimumInvestorPortfolioSizeWarning({
          minimumInvestorPortfolioSize: row.minimumInvestorPortfolioSize,
          dealSharePrice,
          defaultTransactionFeePercent: row.defaultTransactionFeePercent,
          defaultTransactionFeeVatPercent: row.defaultTransactionFeeVatPercent,
          initialTargetShares: row.initialTargetShares,
          lowestInvestmentCapitalInSelection:
            row.lowestInvestmentCapitalInSelection,
        });
        return shouldShowWarning ? '236px' : undefined;
      },
      render: (value: number, row: AutoAllocatingTranche) => {
        const { shouldShowWarning, portfolioAllocationPercent } =
          getMinimumInvestorPortfolioSizeWarning({
            minimumInvestorPortfolioSize: value,
            dealSharePrice,
            defaultTransactionFeePercent: row.defaultTransactionFeePercent,
            defaultTransactionFeeVatPercent:
              row.defaultTransactionFeeVatPercent,
            initialTargetShares: row.initialTargetShares,
            lowestInvestmentCapitalInSelection:
              row.lowestInvestmentCapitalInSelection,
          });

        return (
          <Row justify="flex-end" spacing="xs" centered>
            <Value>
              <NumericFormat
                value={value || ''}
                placeholder="0"
                allowNegative={false}
                decimalScale={2}
                allowLeadingZeros={false}
                thousandSeparator
                customInput={StyledAppTextInput}
                onValueChange={(values) => {
                  handleTrancheFieldChange(
                    row._id,
                    'minimumInvestorPortfolioSize',
                    parseFloat(values.value) || 0,
                  );
                }}
                onFocus={(e) => {
                  if (value === 0) {
                    e.target.select();
                  }
                }}
              />
            </Value>
            {shouldShowWarning && (
              <Tooltip
                title={`This portfolio size implies ${Math.min(
                  100,
                  portfolioAllocationPercent,
                ).toFixed(
                  2,
                )}% of each subscription would be allocated to each deal on average. This deal's share price (plus transaction fee, if applicable) is over ${Math.min(
                  100,
                  portfolioAllocationPercent,
                ).toFixed(
                  2,
                )}% of the smallest subscription in the tranche, which will result in higher portfolio concentration for smaller investors.`}
              >
                <StyledWarningIcon />
              </Tooltip>
            )}
          </Row>
        );
      },
    },
    {
      key: 'maximumSingleCompanyAllocation',
      label: 'Maximum single company allocation',
      render: (value: number, row: AutoAllocatingTranche) => (
        <StyledAppNumberInput
          min={0}
          decimals={2}
          value={value}
          onChange={(newValue) =>
            handleTrancheFieldChange(
              row._id,
              'maximumSingleCompanyAllocation',
              newValue,
            )
          }
          percentEndAdornment
          zeroAsPlaceholder
        />
      ),
      tooltip:
        "This is the maximum amount of any investor's total commitment that can be allocated to one company. If you change the number, it will apply for this and future allocations (it will not re-allocate past deals).",
    },
    {
      key: 'initialTargetShares',
      label: (
        <StyledAppSelectBox
          data={initialTargetSharesOptions}
          valueKey="value"
          labelKey="label"
          fullWidth
          variant="outlined"
          size="small"
          value={initialTargetShareSelected}
          backgroundColor={theme.palette.common.white}
          onChange={(e: SelectChangeEvent<unknown>) => {
            setInitialTargetShareSelected(
              e.target.value as InitialTargetShareOption,
            );
            resetInitialTargetShares();
          }}
        />
      ),
      render: (value: number, row: AutoAllocatingTranche) => (
        <StyledAppNumberInput
          min={0}
          max={
            initialTargetShareSelected ===
            InitialTargetShareOption.InitialTargetAllocationPercent
              ? 100
              : undefined
          }
          decimals={2}
          value={value}
          onChange={(newValue) =>
            handleTrancheFieldChange(row._id, 'initialTargetShares', newValue)
          }
          percentEndAdornment={
            initialTargetShareSelected ===
            InitialTargetShareOption.InitialTargetAllocationPercent
          }
          currencyStartAdornment={
            initialTargetShareSelected ===
              InitialTargetShareOption.InitialTargetInvestment ||
            initialTargetShareSelected ===
              InitialTargetShareOption.InitialTargetInvestmentAndShares
          }
          zeroAsPlaceholder
        />
      ),
    },
    ...(initialTargetShareSelected ===
    InitialTargetShareOption.InitialTargetInvestmentAndShares
      ? [
          {
            key: 'fixedTargetShares',
            label: '',
            render: (value: number, row: AutoAllocatingTranche) => {
              const { flexSharePrice } = calculateTargetValues({
                trancheData: row,
                initialTargetShareSelected,
                dealSharePrice,
              });

              const shouldShowWarning =
                isNumber(flexSharePrice) &&
                (flexSharePrice > dealSharePrice * 1.1 ||
                  flexSharePrice < dealSharePrice * 0.9);

              return (
                <Row justify="flex-end" spacing="xs" centered>
                  <StyledAppNumberInput
                    min={0}
                    onChange={(newValue) => {
                      handleTrancheFieldChange(
                        row._id,
                        'fixedTargetShares',
                        newValue,
                      );
                    }}
                    value={value}
                  />
                  {shouldShowWarning && (
                    <Tooltip
                      title={`The share number and investment amount entered here together imply a share price with over 10% difference to the share price entered at the top of the page.`}
                    >
                      <StyledWarningIcon />
                    </Tooltip>
                  )}
                </Row>
              );
            },
          },
        ]
      : []),
    {
      key: 'defaultTransactionFeePercent',
      label: 'Default transaction fee % (excl. VAT)',
      tooltip:
        'This is the default transaction fee for this allocation. It can be adjusted on a per investor basis after the allocation has been created.',
      render: (value: number, row: AutoAllocatingTranche) => (
        <StyledAppNumberInput
          min={0}
          decimals={2}
          value={value ?? row.defaultTransactionFeePercent ?? 0}
          onChange={(newValue) => {
            handleTrancheFieldChange(
              row._id,
              'defaultTransactionFeePercent',
              newValue,
            );
          }}
          percentEndAdornment
          zeroAsPlaceholder
        />
      ),
    },
    {
      key: 'defaultTransactionFeeVatPercent',
      label: 'Transaction fee VAT %',
      render: (value: number, row: AutoAllocatingTranche) => (
        <StyledAppNumberInput
          min={0}
          decimals={2}
          value={value ?? row.defaultTransactionFeeVatPercent ?? 0}
          disabled={row.defaultTransactionFeePercent === 0}
          onChange={(newValue) => {
            handleTrancheFieldChange(
              row._id,
              'defaultTransactionFeeVatPercent',
              newValue,
            );
          }}
          percentEndAdornment
          zeroAsPlaceholder
        />
      ),
    },
    {
      key: 'targetShares',
      label: 'Target shares',
      tooltip: getTargetTooltip('shares'),
      render: (_, row: AutoAllocatingTranche) => {
        const { targetShares } = calculateTargetValues({
          trancheData: row,
          initialTargetShareSelected,
          dealSharePrice,
        });
        return <Value bold>{targetShares.toLocaleString()}</Value>;
      },
    },
    {
      key: 'targetInvestment',
      label: 'Target investment',
      tooltip: getTargetTooltip('investment'),
      render: (_, row: AutoAllocatingTranche) => {
        const { targetInvestment } = calculateTargetValues({
          trancheData: row,
          initialTargetShareSelected,
          dealSharePrice,
        });
        return <Value bold>{numberToCurrencyString(targetInvestment)}</Value>;
      },
    },
    {
      key: 'targetInvestmentIncTransactionFee',
      label: 'Target investment (inc transaction fee)',
      render: (_, row: AutoAllocatingTranche) => {
        const { targetInvestmentIncTransactionFee } = calculateTargetValues({
          trancheData: row,
          initialTargetShareSelected,
          dealSharePrice,
        });
        return (
          <Value bold>
            {numberToCurrencyString(targetInvestmentIncTransactionFee)}
          </Value>
        );
      },
    },
    {
      key: 'targetAllocationPercent',
      label: 'Target allocation %',
      render: (_, row: AutoAllocatingTranche) => {
        const { targetAllocationPercent } = calculateTargetValues({
          trancheData: row,
          initialTargetShareSelected,
          dealSharePrice,
        });
        return (
          <Value bold>
            {targetAllocationPercent
              ? numberToPercentageStringWithLimits(targetAllocationPercent)
              : '-'}
          </Value>
        );
      },
    },
    {
      key: 'finalDeal',
      label: 'Final deal?',
      tooltip: (
        <>
          After the final deal has been processed, tranche allocations will no
          longer be editable and any residual funds will be transferred to the
          investor cash balance.
          <br />
          <br />
          If this tranche has a live convertible (CLN/ASA/SAFE) within it, do
          not tick this box, even if this deal is the final deal. Instead, tick
          this box when the final convertible converts to shares.
        </>
      ),
      render: (value: boolean, row: AutoAllocatingTranche) => {
        const { targetAllocationPercent } = calculateTargetValues({
          trancheData: row,
          initialTargetShareSelected,
          dealSharePrice,
        });

        const {
          shouldShowInfoOverall,
          shouldShowInfoForPortfolioSizeOne,
          portfolioSizeDifference,
        } = getPortfolioSizeDifferenceWarning({
          minimumInvestorPortfolioSize: row.minimumInvestorPortfolioSize,
          leastDiversifiedTrancheInvestor: row.leastDiversifiedTrancheInvestor,
          remainingTrancheInvestmentCapitalPercent:
            row.remainingTrancheInvestmentCapitalPercent,
          targetAllocationPercent,
          dealSharePrice,
          lowestInvestmentCapitalInSelection:
            row.lowestInvestmentCapitalInSelection,
        });

        return (
          <Row justify="flex-end" spacing="xs" centered>
            <AppCheckBox
              checked={value}
              onChange={(e) => {
                handleTrancheFieldChange(
                  row._id,
                  'finalDeal',
                  e.target.checked,
                );
              }}
              color="primary"
              labelPlacement="start"
            />
            {shouldShowInfoOverall && row.initialTargetShares > 0 && (
              <IconContainer>
                <Tooltip
                  title={
                    <PortfolioSizeTooltip
                      portfolioSizeDifference={portfolioSizeDifference}
                      minimumInvestorPortfolioSize={
                        row.minimumInvestorPortfolioSize
                      }
                      shouldShowInfoForPortfolioSizeOne={
                        shouldShowInfoForPortfolioSizeOne
                      }
                      lowestInvestmentCapitalOfLowestDiversifiedInvestors={
                        row.lowestInvestmentCapitalOfLowestDiversifiedInvestors
                      }
                    />
                  }
                >
                  <StyledWarningIcon />
                </Tooltip>
              </IconContainer>
            )}
          </Row>
        );
      },
    },
  ];
};

export default getAutoAllocationTableFields;
